import { deliveryEstimateFromStore } from "@/lib/helpers/delivery"
import type { ProductPriceWire } from "@/lib/types/price"
import type { StoreDiscount, StoreProduct, StoreProductVariant, StoreRegion } from "@/platform/api/store-contract"
import { getProductPrice } from "@/platform/catalog/products.client"
import type { LastCheckoutDetailsResult } from "@/platform/shopper/carts.client"

const variantPriceWire = (product: { variants?: unknown }, variantId: string): ProductPriceWire | null => {
  const variants = (product.variants ?? []) as { id?: string; price?: ProductPriceWire | null }[]
  return variants.find((variant) => variant.id === variantId)?.price ?? null
}

export const lastCheckoutProductDetails = async (
  product: StoreProduct,
  variant: StoreProductVariant,
  region: StoreRegion,
  discounts: StoreDiscount[]
): Promise<LastCheckoutDetailsResult> => {
  const variantId = variant.id
  const resolvedPrice = variantPriceWire(product, variantId)
  const { variantPrice } = getProductPrice({ product, variantId, region, discounts })
  const price = variantPrice?.calculated_price || variantPrice?.original_price
  if (!price) return { status: "error" }

  const originalPrice = variantPrice?.original_price && variantPrice.original_price !== price ? variantPrice.original_price : undefined
  const freeShipping = discounts.some((discount) => discount.rule.type === "free_shipping")

  const hasMultipleVariants = (product.variants?.length ?? 0) > 1
  // Same in-stock rule as the product page: gate the checkout button and show
  // "Agotado" for out-of-stock variants, while keeping the saved pointer so
  // the user can retry once stock is restored.
  const inStock = Boolean((variant.inventory_quantity ?? 0) > 0 || variant.allow_backorder)

  return {
    status: "ok",
    product: {
      id: product.id,
      title: product.title ?? "",
      handle: product.handle ?? "",
      thumbnail: product.thumbnail ?? undefined,
      variantId,
      variantLabel: hasMultipleVariants && variant.title ? variant.title : undefined,
      price,
      originalPrice,
      inStock,
      freeShipping,
      resolvedPrice,
      delivery: deliveryEstimateFromStore(product.store),
    },
  }
}
